import React, { useEffect, useRef, useState } from "react";
import { Modal, Box, Typography, IconButton, TextField, Chip, Button, CircularProgress, FormControl, Autocomplete, AutocompleteRenderInputParams } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import "./styles.scss"; // importa el SASS
import Grid from "@mui/material/Grid2";
import { getFilePreview } from "src/shared/util/requestDataServer";
import { selectMainState, useDispatch, useSelector } from "src/stateManagement/redux";
import { ListOfActivities } from "./fileattachment";
import { formatDate } from "src/shared/util/functions";
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';

interface FilePreviewModalProps {
  id: string;
  open: boolean;
  onClose: () => void;
}

interface FileData {
  name: string;
  type: string;
  tags: string[];
  activity: string;
  description: string;
  url: string; // signed S3 url
  isImage: boolean;
  signedUrl: string;
  attachedRelatedDate?: string;
  lat?: number;
  long?: number;
}

export const FilePreviewModal: React.FC<FilePreviewModalProps> = ({ id, open, onClose }) => {
  const [file, setFile] = useState<FileData | null>(null);
  const [loading, setLoading] = useState(false);
  const [tags, setTags] = useState<{ name: string; description: string }[]>([]);

  const [listOfFieldActivities, setListOfFieldActivities] = useState<ListOfActivities[]>([])

  const { configModules } = useSelector(selectMainState);

  const imgRef = useRef<HTMLImageElement>(null);

  const [heightOfImage, setHeightOfImage] = useState(0)


  const { fieldActivities } = useSelector(
    (state) => state.location
  );


  const [form, setForm] = useState({
    name: "",
    tags: [] as string[],
    type: "",
    file: null as File | null,
    description: "",
    originFieldActivityId: "",
    locationName: "",
    originFieldActivityDescription: ""
  });

  useEffect(() => {
    const ListOfActivities = fieldActivities.map((activity: any) => {
      const variables = activity?.variables?.find((variable: any) => variable.id === "6d7ad753-bb56-479a-ac5c-d26039113564");
      const executedDate = activity?.executedDate;
      const typeOfActivity = activity?.typeOfActivity;
      const executedBy = activity?.executedBy;

      return {
        id: activity.fieldActivityId,
        label: `${executedDate} - ${typeOfActivity} - ${executedBy}`
      }
    })

    if (Array.isArray(ListOfActivities)) {
      setListOfFieldActivities(ListOfActivities)
    }

  }, [fieldActivities])

  useEffect(() => {
    const filesTypes = configModules?.filesType
    const tags = configModules?.filesTags
    setTags(tags)

  }, [configModules])

  useEffect(() => {
    if (!id || !open) return;
    setLoading(true);
    getFilePreview(id)
      .then(data => {
        const isImage = data.extension.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(data.extension.toLowerCase());

        const attachedRelatedDate = data.variables.find((v: any) => v.name === "attachedRelatedDate")?.value;

        const lat = data.variables.find((v: any) => v.name === "lat")?.value;
        const long = data.variables.find((v: any) => v.name === "long")?.value;

        setFile({
          name: data.fileName,
          type: data.type,
          tags: data.tags,
          activity: data.originActivityId,
          description: data.description,
          url: data.url,
          isImage,
          signedUrl: data.signedUrl,
          attachedRelatedDate: attachedRelatedDate ? attachedRelatedDate : undefined,
          lat: lat && parseFloat(lat),
          long: long && parseFloat(long),
        });
      })
      .finally(() => setLoading(false));
  }, [id, open]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="file-preview-modal">
        <Box className="header">
          <Typography className="title">Previsualizador de Archivos</Typography>
          <IconButton className="close-btn" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        {loading ? (
          <Box className="loading">
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 1 }}>Cargando...</Typography>
          </Box>
        ) : (
          <>
            <Box className="body">
              <Box className="left">
                <TextField label="Nombre" value={file?.name || ""} size="small" disabled fullWidth />
                <TextField label="Tipo" value={file?.type || ""} size="small" disabled fullWidth />
                <Box>
                  <FormControl fullWidth>
                    <Autocomplete
                      multiple
                      options={tags}
                      disabled
                      getOptionLabel={(option) => option.description}
                      value={tags.filter(tag => {
                        return file?.tags.includes(tag.name);
                      })}
                      onChange={(event, newValue) => {
                        setForm(f => ({
                          ...f,
                          tags: newValue.map(tag => tag.name)
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Etiquetas" size="small" fullWidth />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.name}>
                          <Chip label={option.name} size="small" sx={{ mr: 1 }} />
                          {option.description}
                        </li>
                      )}
                    />
                  </FormControl>
                </Box>
                <TextField
                  label="Actividad"
                  value={listOfFieldActivities.find(activity => activity.id === file?.activity)?.label || ""}
                  size="small"
                  disabled
                  fullWidth
                />
                <TextField
                  label="Descripción"
                  value={file?.description || ""}
                  fullWidth
                  disabled
                  multiline
                  rows={4}
                  size="small"

                />
              </Box>
              <Box className="right">
                <IconButton
                  className="download-btn"
                  onClick={() => window.open(file?.signedUrl || "", "_blank")}
                  title="Descargar archivo"
                >
                  <AspectRatioIcon />
                </IconButton>
                {file?.lat && file?.long && (
                  <IconButton
                    className="map-btn"
                     onClick={() => window.open(`https://www.google.com/maps?q=${file.lat},${file.long}`, "_blank")}
                    title="Ver en mapa"
                  >
                    <TravelExploreIcon />
                  </IconButton>
                )}
                {file?.isImage ? (
                  <Grid className="preview-img-external-container">
                    <Grid>
                      <img ref={imgRef} src={file?.signedUrl} alt={file?.name} className="preview-img" />
                      {file.attachedRelatedDate && (
                        <Grid className="preview-img-text-bottom-container">
                          <Typography className="preview-img-text-bottom">
                            {formatDate(file.attachedRelatedDate, "dd-MMM-yy")}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>

                  </Grid>
                ) : (
                  <Grid container className="no-preview-container">
                    <Typography className="no-preview">Vista previa no disponible</Typography>
                  </Grid>
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};